import React from 'react';
import moment from 'moment';

import AlertBanner from '~/components/core/AlertBanner';
import Link from '~/components/core/Atomic/Link/Link';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { serverDateTimeToLocalMoment } from '~/DateTimeUtils';

import { SOR_ISSUES_PAGE_URL } from './api';
import { useSorClaimLockContext } from './SorClaimLockContext';
import TEXTS from './texts';

/**
 * The claim page's account of the change the SoR has yet to confirm on the claim: what it is, how long ago it was
 * made, and that the claim is read-only meanwhile. A banner rather than a chip, since the freeze greys out every write
 * on the page and only this says why. A user who manages the SoR's issues can open the Issues tab from it.
 */
export const SorClaimLockBanner: React.FC<{ className?: string }> = ({ className }) => {
  const { attempt, sorName } = useSorClaimLockContext();
  const canManageSorIssues = useHasPermission({
    action: PERMISSION_ACTIONS.SOR_ISSUES_MANAGE,
    verb: PERMISSION_VERBS.READ,
  });

  if (!attempt) {
    return null;
  }

  // A browser clock behind the server's would otherwise count negative minutes.
  const minutes = Math.max(0, moment().diff(serverDateTimeToLocalMoment(attempt.started_at), 'minutes'));
  const note = attempt.needs_attention
    ? TEXTS.bannerNeedsAttention(sorName, attempt.label, minutes)
    : TEXTS.bannerPending(sorName, attempt.label, minutes);

  return (
    <AlertBanner
      className={className}
      alertType={attempt.needs_attention ? AlertBanner.ALERT_TYPES.ERROR : AlertBanner.ALERT_TYPES.WARNING}
      withIcon
      note={
        <span role="status">
          {note}
          {canManageSorIssues ? (
            <>
              {' '}
              <Link href={SOR_ISSUES_PAGE_URL} target="_blank" rel="noopener noreferrer">
                {TEXTS.viewInIssues}
              </Link>
            </>
          ) : null}
        </span>
      }
    />
  );
};
